import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Text,
  useColorModeValue,
  useToast
} from "@chakra-ui/react";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { flattenZodErrors, SignUpPayload, signUpSchema } from "validations/Validation";
import authRepository from "api/authRepository";


function SignUp() {
  const [show, setShow] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const textColor = useColorModeValue('navy.700', 'white');
  const pageBg = useColorModeValue('white', '#0B1437');
  const cardBg = useColorModeValue('white', '#111C44');
  const inputBg = useColorModeValue('#EEEBFA', '#1B254B');
  const textColorBrand = useColorModeValue('#7062B9', '#A89CE8');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<SignUpPayload>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    userRole: '',
    phone: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof SignUpPayload, string>>>({});


  const handleFieldChange = (field: keyof SignUpPayload, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    const fieldSchema = signUpSchema.shape[field];
    if (fieldSchema) {
      const result = fieldSchema.safeParse(value);
      setErrors((prev) => ({
        ...prev,
        [field]: result.success ? '' : result.error.issues[0].message,
      }));
    }
  };
  const handleSubmit = async () => {
    const result = signUpSchema.safeParse(formData);
    if (loading) return;
    if (!result.success) {
      setErrors(flattenZodErrors(result.error));
      return;
    }

    try {
      setLoading(true);
      const response = await authRepository.Register(result.data);
      console.log(response)
      navigate('/auth/sign-in');
      toast({
        title: 'Registration Successful',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } catch (err) {
      toast({
        title: 'Registration Failed',
        description: 'Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  return (
    <Box minH="100vh" bg={pageBg} position="relative" overflow="hidden">
      <Box
        position="absolute"
        top="0"
        left="0"
        w="100%"
        h="50%"
        bg="#7062B9"
        zIndex={0}
      />

      <Flex
        minH="100vh"
        direction="column"
        position="relative"
        zIndex={1}
      >
        <Flex
          px={{ base: 6, md: 10 }}
          py={6}
        >
          <Text fontSize="lg" fontWeight="700" color="white">
            ServiceCall
          </Text>
        </Flex>

        <Flex flex="1" justify="center" align="center" px={4} py={8}>
          <Box
            bg={cardBg}
            borderRadius="25px"
            p={{ base: 6, md: 8 }}
            w="100%"
            maxW="600px"
            boxShadow="0 10px 30px rgba(112,98,185,0.12)"
          >
            <Heading fontSize="28px" mb="8px" color={textColor}>
              Sign Up
            </Heading>

            <Text fontSize="sm" color="gray.500" mb="18px">
              Enter your email and password to sign up!
            </Text>
            <FormControl mb="15px" >
              <FormLabel fontSize="sm">FirstName </FormLabel>
              <Input
                type="FirstName"
                placeholder="abc"
                bg={inputBg}
                border="1px solid"
                borderColor="transparent"
                borderRadius="14px"
                h="52px"
                fontSize="sm"
                color={textColor}
                value={formData.firstName}
                onChange={(e) => handleFieldChange('firstName', e.target.value)}
                _focus={{ borderColor: "#7062B9", boxShadow: "0 0 0 1px #7062B9" }}
              />
            </FormControl>
            <FormControl mb="15px" >
              <FormLabel fontSize="sm">LastName</FormLabel>
              <Input
                type="email"
                placeholder="xyz"
                bg={inputBg}
                border="1px solid"
                borderColor="transparent"
                borderRadius="14px"
                h="52px"
                fontSize="sm"
                color={textColor}
                value={formData.lastName}
                onChange={(e) => handleFieldChange('lastName', e.target.value)}
                _focus={{ borderColor: "#7062B9", boxShadow: "0 0 0 1px #7062B9" }}
              />
            </FormControl>
            <FormControl mb="15px" >
              <FormLabel fontSize="sm">Email </FormLabel>
              <Input
                type="email"
                placeholder="nectereadmin@gmail.com"
                bg={inputBg}
                border="1px solid"
                borderColor="transparent"
                borderRadius="14px"
                h="52px"
                fontSize="sm"
                color={textColor}
                value={formData.email}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                _focus={{ borderColor: "#7062B9", boxShadow: "0 0 0 1px #7062B9" }}
              />
            </FormControl>
            <FormControl isInvalid={!!errors.userRole}>
              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                mb="8px"
              >
                Role<Text color={textColorBrand}>*</Text>
              </FormLabel>
              <Select
                isRequired
                bg={inputBg}
                color={textColor}
                borderRadius="14px"
                borderColor="gray.200"
                fontSize="sm"
                placeholder="Select your role"
                mb="8px"
                size="lg"
                value={formData.userRole}
                onChange={(e) => handleFieldChange("userRole", e.target.value)}
                _focus={{ borderColor: "#7062B9", boxShadow: "0 0 0 1px #7062B9" }}
              >
                <option value="Admin">Admin</option>
                <option value="User">User</option>
              </Select>
              {errors.userRole && <FormErrorMessage mb="16px">{errors.userRole}</FormErrorMessage>}
            </FormControl>

            <FormControl mb="15px" isInvalid={!!errors.phone} isRequired>
              <FormLabel fontSize="sm">Contact</FormLabel>
              <Input
                type="tel"
                placeholder="+919810962655"
                bg={inputBg}
                border="1px solid"
                borderColor="transparent"
                borderRadius="14px"
                h="52px"
                fontSize="sm"
                color={textColor}
                value={formData.phone}
                onChange={(e) => handleFieldChange("phone", e.target.value)}
                _focus={{ borderColor: "#7062B9", boxShadow: "0 0 0 1px #7062B9" }}
              />
              <FormErrorMessage>{errors.phone}</FormErrorMessage>
            </FormControl>

            <FormControl mb="10px">
              <FormLabel fontSize="sm">Password </FormLabel>
              <InputGroup>
                <Input
                  type={show ? "text" : "password"}
                  placeholder="Enter password"
                  bg={inputBg}
                  border="1px solid"
                  borderColor="transparent"
                  borderRadius="14px"
                  h="52px"
                  fontSize="sm"
                  color={textColor}
                  maxLength={16}
                  value={formData.password}
                  onChange={(e) => handleFieldChange('password', e.target.value)}
                  _focus={{ borderColor: "#7062B9", boxShadow: "0 0 0 1px #7062B9" }}
                />
                <InputRightElement h="100%">
                  <Icon
                    as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    cursor="pointer"
                    color="gray.500"
                    onClick={() => setShow(!show)}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <Button
              w="100%"
              h="50px"
              bg="#7062B9"
              color="white"
              borderRadius="14px"
              fontWeight="600"
              _hover={{ bg: "#5B4FA3" }}
              onClick={handleSubmit}
              isDisabled={loading}
            >
              Sign Up
            </Button>
            <Flex
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              maxW="100%"
              mt="20px"
            >
              <Text color={textColor} fontWeight="400" fontSize="14px">
                Already have an account?
                <NavLink to="/auth/sign-in">
                  <Text
                    color={textColorBrand}
                    as="span"
                    ms="5px"
                    fontWeight="bold"
                  >
                    Sign In
                  </Text>
                </NavLink>
              </Text>
            </Flex>

          </Box>
        </Flex>

        <Text textAlign="center" fontSize="sm" color="gray.500" pb="20px">
          © 2026 ServiceCall
        </Text>
      </Flex>
    </Box>
  );
}

export default SignUp;
